import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { useOutletContext } from 'react-router-dom';
import { User, Mail, Building2, Calendar, Trash2, Download, Loader2, DoorOpen, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import DeleteAccountModal from '@/components/DeleteAccountModal';
import LeaveRequestModal from '@/components/LeaveRequestModal';
import AccountSettings from '@/components/AccountSettings';

export default function MemberProfile() {
  const { user, logout } = useAuth();
  const { asso } = useOutletContext();
  const [showDelete, setShowDelete] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [pending, setPending] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isPresident = user?.role === 'president';

  useEffect(() => {
    if (isPresident) return;
    api.get('/account/deletion-request')
      .then(({ data }) => setPending(data?.statut === 'en_attente'))
      .catch(() => {});
  }, [isPresident]);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get('/account/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mes-donnees-assoperf.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Vos données ont été téléchargées');
    } catch {
      toast.error("Échec de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="member-profile">
      <div className="mb-8 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Profil</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Mon compte</h1>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full grid place-items-center text-white font-display font-black text-lg" style={{ background: 'var(--theme-primary)' }}>
            {(user?.prenom?.[0] || '?') + (user?.nom?.[0] || '')}
          </div>
          <div>
            <div className="font-display text-xl font-bold text-[#0F172A]">{user?.prenom} {user?.nom}</div>
            <div className="text-sm text-[#475569] capitalize">{user?.role}</div>
          </div>
        </div>
        <div className="space-y-3 pt-4 border-t border-[#F1F5F9]">
          <Row icon={Mail} label="Email" value={user?.email} />
          <Row icon={Building2} label="Association" value={asso?.nom} />
          <Row icon={User} label="Rôle" value={<span className="capitalize">{user?.role?.replace('_', ' ')}</span>} />
        </div>
      </div>

      <AccountSettings />

      <div className="card">
        <h2 className="font-display text-lg font-bold text-[#0F172A] mb-3">Sécurité</h2>
        <p className="text-sm text-[#475569] mb-4">Vous pouvez vous déconnecter à tout moment.</p>
        <button onClick={logout} className="btn-secondary" data-testid="profile-logout">Se déconnecter</button>
      </div>

      <div className="card mt-6 border border-[#FECACA] bg-[#FEF2F2]/40">
        <h2 className="font-display text-lg font-bold text-[#B91C1C] mb-2">Zone de danger</h2>
        <p className="text-sm text-[#475569] mb-4">
          {isPresident
            ? "Supprimer votre compte est définitif. Vous devrez d'abord transférer la présidence. Les archives de l'association sont conservées."
            : "Vous ne pouvez pas supprimer votre compte vous-même : envoyez une demande de départ au président, qui la validera avec le bureau."}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportData}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#CBD5E1] text-[#334155] font-semibold text-sm hover:bg-[#F1F5F9] transition-colors disabled:opacity-50"
            data-testid="export-data-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Télécharger mes données (RGPD)
          </button>

          {isPresident ? (
            <button
              onClick={() => setShowDelete(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#FCA5A5] text-[#B91C1C] font-semibold text-sm hover:bg-[#FEE2E2] transition-colors"
              data-testid="delete-account-open"
            >
              <Trash2 className="w-4 h-4" /> Supprimer mon compte
            </button>
          ) : pending ? (
            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] text-[#9A3412] font-semibold text-sm" data-testid="leave-request-pending">
              <Clock className="w-4 h-4" /> Demande de départ en attente de validation
            </span>
          ) : (
            <button
              onClick={() => setShowLeave(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#FDBA74] text-[#B45309] font-semibold text-sm hover:bg-[#FFF7ED] transition-colors"
              data-testid="leave-request-open"
            >
              <DoorOpen className="w-4 h-4" /> Demander à quitter l'association
            </button>
          )}
        </div>
      </div>

      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}
      {showLeave && <LeaveRequestModal onClose={() => setShowLeave(false)} onSent={() => setPending(true)} />}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#F1F5F9] grid place-items-center text-[#475569]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 flex items-center justify-between">
        <span className="text-xs text-[#475569] uppercase tracking-wider font-bold">{label}</span>
        <span className="text-sm font-semibold text-[#0F172A]">{value}</span>
      </div>
    </div>
  );
}
